import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';

interface EscaloneLoadingProps {
  title?: string;
  subtitle?: string;
  description?: string;
  steps?: string[];
}

const EscaloneLoading: React.FC<EscaloneLoadingProps> = ({
  title,
  subtitle = 'ambiente de trabalho',
  description,
  steps: customSteps
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = customSteps || [
    'Inicializando sistema',
    'Conectando ao servidor',
    'Carregando dados',
    'Finalizando'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100;
        return prev + Math.random() * 2 + 0.5;
      });
    }, 120);

    const stepTimer = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, 2500);

    return () => {
      clearInterval(timer);
      clearInterval(stepTimer);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center overflow-hidden z-[9999]">
      {/* Background pattern da landing page */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5"></div>
      
      {/* Animated orbs mais sutis */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-400/8 to-purple-500/8 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-purple-400/8 to-blue-400/8 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
      
      {/* Partículas flutuantes */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-pulse"
            style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${20 + Math.random() * 60}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left side - Conteúdo textual */}
          <div className="text-center lg:text-left">
            {/* Badge igual da landing */}
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700 mb-6">
              <Shield className="w-4 h-4" /> Sistema carregando
            </div>
            
            {/* Título principal */}
            <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight mb-6">
              {title || 'Preparando seu '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{subtitle}</span>
            </h1>
            
            {/* Descrição */}
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {description || 'Estamos configurando sua plataforma completa para organizar equipes, automatizar processos e dar visibilidade total aos seus negócios.'}
            </p>
            
            {/* Status atual */}
            <div className="mb-8">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </div>
                <span className="text-lg font-medium text-gray-900">{steps[currentStep]}</span>
              </div>
              
              {/* Step indicators */}
              <div className="flex justify-center lg:justify-start gap-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1 rounded-full transition-all duration-500 ${
                      index <= currentStep 
                        ? 'w-8 bg-gradient-to-r from-blue-600 to-purple-600' 
                        : 'w-4 bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Progresso</span>
                <span className="text-sm font-bold text-gray-900">{Math.round(Math.min(progress, 100))}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all duration-300 ease-out relative"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            {/* Info adicional */}
            <p className="text-sm text-gray-500 flex items-center justify-center lg:justify-start gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Sistema online • Conectando com segurança
            </p>
          </div>
          
          {/* Right side - E orbitando modernizado */}
          <div className="relative flex items-center justify-center">
            {/* Container do preview igual landing page */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
              <div className="relative rounded-3xl bg-white p-4 shadow-2xl border border-gray-200">
                <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-12 flex items-center justify-center">
                  
                  {/* Logo E com movimento orbital */}
                  <div className="relative w-32 h-32">
                    {/* Órbitas sutis */}
                    <div className="absolute inset-0 rounded-full border border-dashed border-white/20 animate-pulse" style={{ animationDuration: '4s' }} />
                    <div className="absolute inset-4 rounded-full border border-dotted border-blue-400/30 animate-pulse" style={{ animationDuration: '3s', animationDelay: '1s' }} />
                    
                    {/* E orbitando */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="relative animate-orbit">
                        <div className="absolute -translate-x-8 -translate-y-8">
                          <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-900 to-black text-white flex items-center justify-center shadow-2xl border border-white/20">
                            <span className="text-white font-bold text-2xl">E</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Centro */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-pulse" />
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {/* CSS para animação orbital */}
      <style>{`
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(40px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(40px) rotate(-360deg); }
        }
        
        .animate-orbit {
          animation: orbit 8s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default EscaloneLoading;
